import { useEffect } from 'react';
import { readPlayerState } from '@/utils/playerState';

/**
 * Auto-restore the previously-playing track after iOS Safari kills the
 * page during a long screen-lock (or any other hard reload).
 *
 * Call this from any page that mounts <AudioPlayer/>:
 *
 *     useRestorePlayer(tracks, playingTrack, setPlayingTrack);
 *
 * Reads the persisted player state from localStorage. If it contains a
 * trackId that exists in the current page's `tracks` list and the user did
 * not explicitly close the player (X button), it sets `playingTrack` once
 * — bringing the player UI back automatically.
 *
 * - Only restores if `playingTrack` is currently null (don't override a
 *   live selection).
 * - Only restores tracks with cloud audio available.
 * - Idempotent: re-renders won't re-restore once playingTrack is set.
 */
export default function useRestorePlayer(tracks, playingTrack, setPlayingTrack) {
  useEffect(() => {
    if (playingTrack) return;
    if (!Array.isArray(tracks) || tracks.length === 0) return;

    const saved = readPlayerState();
    if (!saved || saved.closed || !saved.trackId) return;

    const match = tracks.find((t) => t && t.id === saved.trackId);
    if (!match) return;
    if (!match.has_audio && !match.storage_path) return;

    setPlayingTrack(match);
  }, [tracks, playingTrack, setPlayingTrack]);
}
